import { BrowserModule, HammerModule, Meta } from '@angular/platform-browser';
import { ErrorHandler, LOCALE_ID, NgModule, CUSTOM_ELEMENTS_SCHEMA  } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { AppRoutes } from './app.routing';
import { AppComponent } from './app.component';
import * as Sentry from "@sentry/angular-ivy";

import { FullComponent } from './layouts/full/full.component';
import { PartialComponent } from './layouts/partial/partial.component';
import { AppBlankComponent } from './layouts/blank/blank.component';

import { VerticalAppHeaderComponent } from './layouts/full/vertical-header/vertical-header.component';
import { VerticalAppSidebarComponent } from './layouts/full/vertical-sidebar/vertical-sidebar.component';
import { HorizontalAppHeaderComponent } from './layouts/full/horizontal-header/horizontal-header.component';
import { HorizontalAppSidebarComponent } from './layouts/full/horizontal-sidebar/horizontal-sidebar.component';
import { AppBreadcrumbComponent } from './layouts/full/breadcrumb/breadcrumb.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BannerComponent } from './layouts/full/banner/banner.component';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { SharedModule } from './shared/shared.module';
import { SpinnerComponent } from './components/spinner/spinner.component';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  ConfirmDialogService,
  FetchService,
  StateService,
  WindowRefService,
} from './services';
import { CookieService } from 'ngx-cookie-service';
import { IdentityWebApiClient } from 'identity-webapi-client';
import { SituAnalyticsWebApiClient } from 'situanalytics-webapi-client';
import { SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';
import { SituViewWebApiClient } from 'situview-webapi-client';
import { ConfigService } from './services/config.service';
import { registerLocaleData } from '@angular/common';
import localeEnAu from '@angular/common/locales/en-AU';
import {
  AddressAutoCompleteComponent,
  ArtifactViewComponent,
  AssetUploadComponent,
  AuthCallbackComponent,
  AuthLoggedOutComponent,
  ConfirmDialogComponent,
  FacadeHomesDialogComponent,
  DateRangeComponent,
  DownloadReportDialogComponent,
  FloorPlanComponent,
  HomeConfigSlimComponent,
  HomeConfigListComponent,
  HomeConfigListCardComponent,
  HomeConfigSettingsComponent,
  HomeConfigShortcutComponent,
  IfcFileUploadStatusComponent,
  IfcFileListComponent,
  PanoramaComponent,
  PdfViewerComponent,
  ProductCardComponent,
  NewProductCardComponent,
  ProductSpecificationTreeComponent,
  ProductSpecificationFilterComponent,
  ProductSpecificationEditComponent,
  RenderTaskViewComponent,
  ViewAsComponent,
  TagPickerComponent,
  EditTagComponent,
  SituapiRequestCardComponent,
  StyleConfigComponent,
  AccessoryBundleConfigComponent,
  SourceArtifactsMenuComponent,
  ArtifactMetadataMenuComponent,
  CopyValueComponent,
  RecipeEventSummaryCardComponent,
  StyleTemplateCardComponent,
  CustomExpansionPanelComponent,
  ArtifactViewerDialogComponent,
  ProductSelectorDialogComponent,
  SelectProductDialogComponent,
  FileVaultSelectorDialogComponent,
  SelectArtifactDialogComponent,
  SelectLogoMenu,
  ArtifactTreeComponent,
  AssetValidationComponent,
  ProductSpecificationTreeDialogComponent,
  ProductWorkflowIconComponent,
  WorkflowTriggerDialogComponent,
  CarouselComponent,
  VideoDialogComponent,
  TagChipComponent,
  QuickViewDialogComponent,
  VersionNotificationComponent
} from './components';
import { CompanySidebarComponent } from './components/company-sidebar/company-sidebar.component';
import {
  AddressTestComponent,
  ApproveNewUserComponent,
  ArtifactListComponent,
  BannerTestComponent,
  CachedHomesComponent,
  CompaniesAddEditComponent,
  CompaniesListComponent,
  CompanyComponent,
  CompanyReportSubscriptionsComponent,
  CredentialsComponent,
  GettingStartedComponent,
  HomeAddComponent,
  HomeDetailComponent,
  HomeListComponent,
  HomeViewerComponent,
  IfcReportComponent,
  LandingPageComponent,
  LegalAgreementComponent,
  PartnershipActionConfirmationDialogComponent,
  PartnershipComponent,
  PartnershipRequestComponent,
  PartnershipsListComponent,
  PdfViewerTestComponent,
  ProductImpressionsComponent,
  ProductImpressionsGraphComponent,
  ProductDetailsReportComponent,
  ThemeProductProgressReportComponent,
  ProductDetailsTopBarComponent,
  ProductDetailsTabComponent,
  ProductThemesTabComponent,
  ProductWorkflowHistoryTabComponent,
  FileVaultBrowserTabComponent,
  ProductPublishTabComponent,
  ProductDiscoverTabComponent,
  ProductsAddEditComponent,
  NewProductEditComponent,
  RequestedProductsListComponent,
  ProductCategorization,
  ProductReplacementTags,
  ProductSubstitutionsComponent,
  ProductsListComponent,
  ProductsConfirmationDialogComponent,
  ProductImportPreviewComponent,
  RangesListComponent,
  RangeListComponent,
  RangesAddEditComponent,
  RenderedOutputListComponent,
  RendererListComponent,
  SituApiManageClientsComponent,
  SituApiAddClientComponent,
  SituApiActionConfirmationDialogComponent,
  UnauthorizedComponent,
  UploadIfcFilesDialogComponent,
  UserCopyDialogComponent,
  UserDetailComponent,
  UserDetailsComponent,
  UserListComponent,
  VerifyEmailComponent,
  ViewHomeAnalyticsComponent,
  WarpEnvironmentsComponent,
  WarpProductEnvironmentsComponent,
  InteractiveThemeEditor,
  ThemesListComponent,
  IfcRoomsConfigComponent,
  IfcFurnishingsConfigComponent,
  IfcBuildingMaterialsConfigComponent,
  IfcStoreysConfigComponent,
  SituFacadeComponent,
  SituFacadeReadonlyComponent,
  SituBrowseComponent,
  SituViewComponent,
  ScratchTemplateDialogComponent,
  SituReadonlyFactoryComponent,
  RecipeEventSummaryComponent,
  RecipeEventSummaryListComponent,
  RecipeProgressBarComponent,
  FacadeReportComponent,
  StyleTemplateListComponent,
  StyleTemplateEditComponent,
  PlatformUsageComponent,
  ReleaseListComponent,
  ReleaseOverviewComponent,
  ReleaseEditComponent,
  ThemesImportedLinesComponent,
  ThemesImportedProductReplacementsComponent,
  ThemesDetailComponent,
  ProductCatalogComponent,
  ProductCatalogListComponent,
  ProductDiscoverComponent,
  ThemesAddComponent,
  ThemesCopyComponent,
  RequestedSupplierListComponent,
  HomeDetailsListComponent,
  PlaceholderTemplatesComponent,
  BuilderOptionsComponent,
  TrainingComponent,
  PlaceholderTemplateDialogComponent,
  SituProductReadonlyComponent,
  StyleTemplateCopyDialogComponent,
  SituPlanReadonlyComponent,
  ThumbProductCardComponent,
  ReleaseStyleTemplateDialogComponent,
  SituRenderReadonlyComponent,
  SituReelStripComponent,
  SituReelsListComponent,
  SituReelEditDialogComponent,
  SituReelItemPickerDialogComponent,
  SituReelProductPickerDialogComponent,
  SituReelStartViewPickerDialogComponent,
  ProductDownloadDialogComponent,
  ReleaseIssueDialogComponent,
  ReleaseIssuesReportComponent,
  PartnershipCardComponent,
  SituConnectComponent,
  SituConnectConfigListComponent,
  SituConnectConfigListCardComponent,
  TagListComponent,
  TagEditDialogComponent,
  AccessoryBundleListComponent,
  AccessoryBundleEditComponent,
  AccessoryBundleAddComponent,
  MonthlyBillingSummaryComponent,
  RenderSummaryReportComponent,
  BuilderRepresentationReportComponent,
  SupplierDashboardComponent,
  FileVaultBrowserComponent
} from './views';
import { SituWarpApiClient } from 'situ-warpapi-client';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { EnvironmentsListComponent } from './views/environments/environments-list/environments-list.component';
import { OverridesAddEditComponent } from './views/environments/overrides-add-edit/overrides-add-edit.component';
import { RendererDashboardComponent } from './views/admin/renderers/renderer-dashboard/renderer-dashboard.component';
import { QueueListComponent } from './views/admin/renderers/queue-list/queue-list.component';
import { AddQueueDialogComponent } from './views/admin/renderers/add-queue-dialog/add-queue-dialog.component';
import { RendererEditDialogComponent } from './views/admin/renderers/renderer-edit-dialog/renderer-edit-dialog.component';
import { RendererPoolListComponent } from './views/admin/renderers/renderer-pool-list/renderer-pool-list.component';
import { RendererPoolDialogComponent } from './views/admin/renderers/renderer-pool-dialog/renderer-pool-dialog.component';
import { StyleTreeComponent } from './views/admin/styles/style-tree/style-tree.component';
import { StyleCategoryEditComponent } from './views/admin/styles/style-category-edit/style-category-edit.component';
import { StyleCardComponent } from './components/style-card/style-card.component';
import { SituViewReadonlyComponent } from './views/render-product/situ-view-readonly/situ-view-readonly.component';
import { RenameDialogComponent } from './components/rename-dialog/rename-dialog.component';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { ReleaseIssueCardComponent } from './views/admin/product-releases/release-issue-card/release-issue-card.component';
import { ReleaseIssueListComponent } from './views/admin/product-releases/release-issue-list/release-issue-list.component';
import { CreateBrandDialogComponent } from './views/products/create-brand-dialog/create-brand-dialog/create-brand-dialog.component';
import { BrandsComponent } from './views/products/brands/brands.component';
import { MarketingComponent, TruncatePipe } from './views/products/marketing/marketing.component';
import { TileContentComponent } from './views/products/tile-content/tile-content.component';
import { FileMapEditorComponent } from './views/products/marketing/file-map-editor/file-map-editor.component';

registerLocaleData(localeEnAu, 'en-AU');

export function situAnalyticsWebApiClientFactory(configService: ConfigService, fetchService: FetchService) {
  return new SituAnalyticsWebApiClient(configService.config.analyticsWebApiBaseUrl, fetchService);
}

export function situViewWebApiClientFactory(configService: ConfigService, fetchService: FetchService) {
  return new SituViewWebApiClient(configService.config.situViewWebApiBaseUrl, fetchService);
}

export function identityWebApiClientFactory(configService: ConfigService, fetchService: FetchService) {
  return new IdentityWebApiClient(configService.config.identityApiBaseUrl, fetchService);
}

export function renderServiceWebApiClientFactory(configService: ConfigService, fetchService: FetchService) {
  return new SituRenderServiceWebApiClient(configService.config.renderServiceApiBaseUrl, fetchService);
}

export function warpApiClientFactory(configService: ConfigService, fetchService: FetchService) {
  return new SituWarpApiClient(configService.config.warpApiBaseUrl, fetchService);
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AddressAutoCompleteComponent,
    AddressTestComponent,
    AppBlankComponent,
    AppBreadcrumbComponent,
    AppComponent,
    ApproveNewUserComponent,
    ArtifactListComponent,
    ArtifactViewComponent,
    AssetUploadComponent,
    AuthCallbackComponent,
    AuthLoggedOutComponent,
    BannerComponent,
    BannerTestComponent,
    CachedHomesComponent,
    CompaniesAddEditComponent,
    CompaniesListComponent,
    CompanyComponent,
    CompanyReportSubscriptionsComponent,
    CompanySidebarComponent,
    ConfirmDialogComponent,
    CredentialsComponent,
    DateRangeComponent,
    DownloadReportDialogComponent,
    EditTagComponent,
    FloorPlanComponent,
    FullComponent,
    PartialComponent,
    GettingStartedComponent,
    HomeConfigSlimComponent,
    HomeConfigListComponent,
    HomeConfigListCardComponent,
    HomeConfigSettingsComponent,
    HomeConfigShortcutComponent,
    HomeListComponent,
    HomeViewerComponent,
    HorizontalAppHeaderComponent,
    HorizontalAppSidebarComponent,
    IfcRoomsConfigComponent,
    IfcBuildingMaterialsConfigComponent,
    IfcStoreysConfigComponent,
    IfcFurnishingsConfigComponent,
    IfcFileListComponent,
    IfcFileUploadStatusComponent,
    IfcReportComponent,
    LandingPageComponent,
    LegalAgreementComponent,
    PanoramaComponent,
    PartnershipActionConfirmationDialogComponent,
    PartnershipCardComponent,
    PartnershipComponent,
    PartnershipRequestComponent,
    PartnershipsListComponent,
    PdfViewerComponent,
    PdfViewerTestComponent,
    ProductCardComponent,
    NewProductCardComponent,
    ProductSpecificationTreeComponent,
    ProductSpecificationFilterComponent,
    ProductSpecificationEditComponent,
    ProductImportPreviewComponent,
    ProductImpressionsComponent,
    ProductImpressionsGraphComponent,
    ProductDetailsReportComponent,
    ThemeProductProgressReportComponent,
    ProductDetailsTopBarComponent,
    ProductDetailsTabComponent,
    ProductThemesTabComponent,
    ProductWorkflowHistoryTabComponent,
    FileVaultBrowserComponent,
    FileVaultBrowserTabComponent,
    ProductPublishTabComponent,
    ProductDiscoverTabComponent,
    ProductsAddEditComponent,
    NewProductEditComponent,
    ProductCategorization,
    ProductReplacementTags,
    ProductSubstitutionsComponent,
    ProductsListComponent,
    ProductsConfirmationDialogComponent,
    RangesListComponent,
    RangeListComponent,
    RangesAddEditComponent,
    RenderedOutputListComponent,
    RendererDashboardComponent,
    RendererListComponent,
    RenderTaskViewComponent,
    SituApiManageClientsComponent,
    SituApiAddClientComponent,
    SituApiActionConfirmationDialogComponent,
    SpinnerComponent,
    SourceArtifactsMenuComponent,
    ArtifactMetadataMenuComponent,
    CopyValueComponent,
    StyleCardComponent,
    StyleConfigComponent,
    AccessoryBundleConfigComponent,
    UnauthorizedComponent,
    UploadIfcFilesDialogComponent,
    UserCopyDialogComponent,
    UserDetailComponent,
    UserDetailsComponent,
    UserListComponent,
    VerifyEmailComponent,
    VerticalAppHeaderComponent,
    VerticalAppSidebarComponent,
    ViewAsComponent,
    ViewHomeAnalyticsComponent,
    WarpEnvironmentsComponent,
    WarpProductEnvironmentsComponent,
    HomeAddComponent,
    HomeDetailComponent,
    TagPickerComponent,
    InteractiveThemeEditor,
    ThemesListComponent,
    SituapiRequestCardComponent,
    SituFacadeComponent,
    SituFacadeReadonlyComponent,
    SituBrowseComponent,
    SituReadonlyFactoryComponent,
    SituViewComponent,
    ScratchTemplateDialogComponent,
    SituViewReadonlyComponent,
    EnvironmentsListComponent,
    OverridesAddEditComponent,
    QueueListComponent,
    AddQueueDialogComponent,
    RendererEditDialogComponent,
    RendererPoolListComponent,
    RendererPoolDialogComponent,
    StyleTreeComponent,
    StyleCategoryEditComponent,
    RecipeEventSummaryComponent,
    RecipeEventSummaryListComponent,
    RecipeProgressBarComponent,
    FacadeReportComponent,
    RenderSummaryReportComponent,
    BuilderRepresentationReportComponent,
    SupplierDashboardComponent,
    RecipeEventSummaryCardComponent,
    StyleTemplateEditComponent,
    StyleTemplateListComponent,
    StyleTemplateCardComponent,
    CustomExpansionPanelComponent,
    PlatformUsageComponent,
    ReleaseListComponent,
    ReleaseOverviewComponent,
    ReleaseEditComponent,
    ArtifactViewerDialogComponent,
    ProductSpecificationTreeDialogComponent,
    ProductWorkflowIconComponent,
    WorkflowTriggerDialogComponent,
    ThemesImportedLinesComponent,
    ThemesImportedProductReplacementsComponent,
    ThemesDetailComponent,
    ThemesAddComponent,
    ThemesCopyComponent,
    ProductCatalogComponent,
    ProductCatalogListComponent,
    ProductDiscoverComponent,
    RequestedProductsListComponent,
    ProductSelectorDialogComponent,
    SelectProductDialogComponent,
    FileVaultSelectorDialogComponent,
    SelectArtifactDialogComponent,
    SelectLogoMenu,
    ArtifactTreeComponent,
    AssetValidationComponent,
    RenameDialogComponent,
    RequestedSupplierListComponent,
    HomeDetailsListComponent,
    PlaceholderTemplatesComponent,
    BuilderOptionsComponent,
    TrainingComponent,
    PlaceholderTemplateDialogComponent,
    SituProductReadonlyComponent,
    SituPlanReadonlyComponent,
    StyleTemplateCopyDialogComponent,
    ThumbProductCardComponent,
    ReleaseStyleTemplateDialogComponent,
    FacadeHomesDialogComponent,
    CarouselComponent,
    SituRenderReadonlyComponent,
    SituReelStripComponent,
    SituReelsListComponent,
    SituReelEditDialogComponent,
    SituReelItemPickerDialogComponent,
    SituReelProductPickerDialogComponent,
    SituReelStartViewPickerDialogComponent,
    ReleaseIssueDialogComponent,
    ReleaseIssueCardComponent,
    SituConnectComponent,
    ReleaseIssueListComponent,
    ReleaseIssuesReportComponent,
    SituConnectConfigListComponent,
    SituConnectConfigListCardComponent,
    TagListComponent,
    TagEditDialogComponent,
    TagChipComponent,
    VideoDialogComponent,
    CreateBrandDialogComponent,
    BrandsComponent,
    MarketingComponent,
    TileContentComponent,
    TruncatePipe,
    FileMapEditorComponent,
    TagChipComponent,
    QuickViewDialogComponent,
    VersionNotificationComponent,
    AccessoryBundleListComponent,
    AccessoryBundleEditComponent,
    AccessoryBundleAddComponent,
    MonthlyBillingSummaryComponent
  ],
  entryComponents: [
    DownloadReportDialogComponent,
    ConfirmDialogService
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    ScrollingModule,
    SharedModule,
    RouterModule.forRoot(AppRoutes, {
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
    }),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    HammerModule,
    LazyLoadImageModule
  ],
  providers: [
    CookieService,
    WindowRefService,
    Meta,
    ConfirmDialogService,
    {
      provide: SituAnalyticsWebApiClient,
      useFactory: situAnalyticsWebApiClientFactory,
      deps: [ConfigService, FetchService]
    },
    {
      provide: SituViewWebApiClient,
      useFactory: situViewWebApiClientFactory,
      deps: [ConfigService, FetchService]
    },
    {
      provide: IdentityWebApiClient,
      useFactory: identityWebApiClientFactory,
      deps: [ConfigService, FetchService]
    },
    {
      provide: SituRenderServiceWebApiClient,
      useFactory: renderServiceWebApiClientFactory,
      deps: [ConfigService, FetchService]
    },
    {
      provide: SituWarpApiClient,
      useFactory: warpApiClientFactory,
      deps: [ConfigService, FetchService]
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: true
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    { provide: LOCALE_ID, useValue: 'en-AU' },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3000 } }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule { }
